import React, { useState } from 'react';
import { Copy, CheckCircle, Clock } from 'lucide-react';
import UserSearch from './UserSearch';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';

interface UserInfo {
  user_id: string;
  email: string;
  email_verified: boolean;
  name?: string;
  phone_number?: string;
  created_at?: string;
  updated_at?: string;
}

interface ResetPasswordResponse {
  reset_url: string;
  token: string;
  expires_in_minutes: number;
}

const AdminPasswordResetSection: React.FC = () => {
  const authenticatedApi = useAuthenticatedApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetResult, setResetResult] = useState<ResetPasswordResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const handleUserSelect = async (userInfo: UserInfo) => {
    setLoading(true);
    setError(null);
    setResetResult(null);
    setCopied(false);

    try {
      const response = await authenticatedApi.post<ResetPasswordResponse>(
        apiEndpoints.admin.resetUserPassword,
        { identifier: userInfo.user_id },
      );
      setResetResult(response);
    } catch (err) {
      console.error('Admin password reset error:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate reset link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!resetResult) return;
    try {
      await navigator.clipboard.writeText(resetResult.reset_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for insecure contexts
      const textarea = document.createElement('textarea');
      textarea.value = resetResult.reset_url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold text-ui-text-primary mb-6">
        Password Reset
      </h2>

      <p className="text-ui-text-secondary mb-4">
        Search for a user to generate a one-time password reset link. The link can be shared with the user directly.
      </p>

      <UserSearch
        onUserSelect={handleUserSelect}
        disabled={loading}
        label="User ID or Email"
        placeholder="Enter user ID or email (e.g., user@example.com)"
        buttonText="Generate Reset Link"
        helpText={
          <>
            <p>Enter a <strong>user ID</strong> or <strong>email address</strong> to find the user.</p>
            <p>• A one-time reset link will be generated after selecting the user</p>
            <p>• Any existing unused reset link for this user will be replaced</p>
          </>
        }
      />

      {error && (
        <div className="mt-4 flex items-center p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
        </div>
      )}

      {resetResult && (
        <div className="mt-6 space-y-4">
          <div className="flex items-center p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <CheckCircle size={16} className="text-green-600 dark:text-green-400 mr-2 flex-shrink-0" />
            <p className="text-green-700 dark:text-green-300 text-sm font-medium">Reset link generated successfully</p>
          </div>

          <div className="bg-ui-background-secondary border border-ui-border-primary rounded-lg p-4 space-y-3">
            <label className="block text-sm font-medium text-ui-text-secondary">
              Reset Link
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                readOnly
                value={resetResult.reset_url}
                className="flex-1 px-3 py-2 border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary font-mono text-sm"
              />
              <button
                onClick={handleCopyLink}
                className="inline-flex items-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
                <span className="ml-2">{copied ? 'Copied' : 'Copy'}</span>
              </button>
            </div>

            <div className="flex items-center text-sm text-ui-text-tertiary">
              <Clock size={14} className="mr-1.5" />
              Link expires in {resetResult.expires_in_minutes} minutes
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPasswordResetSection;
